import { useEffect, useRef, useState } from 'react';

export interface FileDropTargetOptions {
  /**
   * When false the hook still swallows drops (so the browser cannot navigate
   * away to the dropped file) but raises no drag state and never calls onDrop.
   */
  enabled: boolean;
  onDrop: (files: File[]) => void;
}

/**
 * True for a drag carrying files. Necessary but not sufficient for an upload:
 * a photo tile dragged to reorder also reports files (see `dragstart` below),
 * so provenance decides. An image dragged in from another browser tab carries
 * only text/uri-list and is excluded here.
 */
function isFileDrag(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files');
}

/**
 * Window-level file drop target. Listeners live for as long as the calling
 * component is mounted, so this is page-scoped despite being on `window`:
 * nothing is registered globally and navigating away detaches everything.
 */
export interface FileDropTargetState {
  isDragActive: boolean;
}

export function useFileDropTarget({
  enabled,
  onDrop
}: FileDropTargetOptions): FileDropTargetState {
  const [isDragActive, setIsDragActive] = useState(false);

  // dragleave fires on every crossing into a child element, so count enters
  // against leaves rather than tracking a boolean.
  const dragDepth = useRef(0);

  // Set for the lifetime of a drag that started inside the document — i.e. a
  // photo tile being dragged to reorder. Such a drag hands the browser the
  // tile's <img>, which Chromium and WebKit expose as a promised file: 'Files'
  // lands in dataTransfer.types and the image arrives as a real File on drop.
  // So the payload alone cannot distinguish a reorder from a photo dragged in
  // from Finder — only provenance can, and an OS file drag never fires
  // dragstart in this document.
  const isInPageDrag = useRef(false);

  // Held in a ref so a parent re-render with a fresh callback identity does not
  // detach and reattach the listeners mid-drag.
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;

  useEffect(() => {
    // An `enabled` flip mid-drag must not leave the affordance stuck on: the
    // cleanup resets the depth counter, but the OS may never deliver another
    // dragleave/drop to clear the state on its own.
    if (!enabled) setIsDragActive(false);

    // Capture phase: a drag source that stops propagation on dragstart must
    // not be able to hide its provenance from this hook.
    const handleDragStart = () => {
      isInPageDrag.current = true;
      // The gesture may already have raised the affordance — a tile drag emits
      // dragenter before the state is known — so stand it back down.
      dragDepth.current = 0;
      setIsDragActive(false);
    };

    const handleDragEnd = () => {
      isInPageDrag.current = false;
      dragDepth.current = 0;
    };

    const handleDragEnter = (event: DragEvent) => {
      if (isInPageDrag.current || !isFileDrag(event)) return;
      dragDepth.current += 1;
      if (enabled) setIsDragActive(true);
    };

    const handleDragOver = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      // Required by the drag-and-drop spec for `drop` to fire at all, and it
      // is what stops the browser navigating to the file when disabled. An
      // in-page drag needs it too: a reorder released outside the grid would
      // otherwise navigate the tab to the photo's own URL.
      event.preventDefault();
    };

    const handleDragLeave = (event: DragEvent) => {
      if (isInPageDrag.current || !isFileDrag(event)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setIsDragActive(false);
    };

    const handleDrop = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      dragDepth.current = 0;
      setIsDragActive(false);
      // The grid's own card handler owns a reorder drop; this one must not
      // also read its payload as an upload. Cleared here as well as on dragend
      // so a drag that somehow never reports its end cannot wedge the flag on
      // and mute uploads for the rest of the page's life.
      const wasInPageDrag = isInPageDrag.current;
      isInPageDrag.current = false;
      if (wasInPageDrag || !enabled) return;
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length > 0) onDropRef.current(files);
    };

    window.addEventListener('dragstart', handleDragStart, true);
    window.addEventListener('dragend', handleDragEnd, true);
    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragstart', handleDragStart, true);
      window.removeEventListener('dragend', handleDragEnd, true);
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
      dragDepth.current = 0;
      isInPageDrag.current = false;
    };
  }, [enabled]);

  return { isDragActive };
}
